import { useEffect, useCallback } from 'react';

interface MemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
  linkedinUrl: string;
}

export default function MemberModal({ isOpen, onClose, name, role, bio, imageUrl, linkedinUrl }: MemberModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-background-50 border border-secondary-200/60 animate-[fadeIn_0.3s_ease-out]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/90 hover:bg-background-100 border border-secondary-200/60 text-foreground-500 hover:text-foreground-900 transition-all duration-200 cursor-pointer"
          aria-label="Cerrar"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Image */}
          <div className="md:col-span-2 h-64 md:h-full md:min-h-[420px]">
            <img
              src={imageUrl}
              alt={name}
              className="w-full h-full object-cover object-center rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none"
            />
          </div>

          {/* Content */}
          <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-center">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold mb-3 whitespace-nowrap w-fit">
              {role}
            </span>
            <h3 id="modal-title" className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              {name}
            </h3>
            <p className="text-foreground-600 text-sm leading-relaxed text-justify mb-5">
              {bio}
            </p>
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#004182] transition-colors duration-200 whitespace-nowrap w-fit cursor-pointer"
              >
                <i className="ri-linkedin-fill text-base" />
                Ver perfil en LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}